import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type SelectableTileValue = string | number;

export interface SelectableTileOption {
  value: SelectableTileValue;
  label: string;
  icon?: string;
  accentColor?: string;
  helperText?: string;
  disabled?: boolean;
}

export interface SelectableTileOptionView extends SelectableTileOption {
  selected: boolean;
}

@Component({
  selector: 'app-selectable-tile-group',
  templateUrl: './selectable-tile-group.component.html',
  styleUrls: ['./selectable-tile-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectableTileGroupComponent),
      multi: true
    }
  ]
})
export class SelectableTileGroupComponent implements ControlValueAccessor, OnChanges {
  @Input() options: SelectableTileOption[] = [];
  @Input() density: 'default' | 'compact' = 'default';
  @Input() multiple = true;
  @Input() ariaLabel = 'Selectable options';
  @Input() disabled = false;
  @Output() selectionChange = new EventEmitter<SelectableTileValue[]>();

  selectedValues: SelectableTileValue[] = [];
  optionViews: SelectableTileOptionView[] = [];

  constructor(private changeDetectorRef: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options) {
      this.updateOptionViews();
    }
  }

  onChange: (value: SelectableTileValue[]) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(value: SelectableTileValue[] | SelectableTileValue | null): void {
    if (Array.isArray(value)) {
      this.selectedValues = [...value];
      this.updateOptionViews();
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.selectedValues = value === null || value === undefined ? [] : [value];
    this.updateOptionViews();
    this.changeDetectorRef.markForCheck();
  }

  registerOnChange(fn: (value: SelectableTileValue[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.changeDetectorRef.markForCheck();
  }

  toggleOption(option: SelectableTileOptionView): void {
    if (this.disabled || option.disabled) {
      return;
    }

    const selected = option.selected;
    if (this.multiple) {
      this.selectedValues = selected
        ? this.selectedValues.filter(value => value !== option.value)
        : [...this.selectedValues, option.value];
    } else {
      this.selectedValues = selected ? [] : [option.value];
    }

    this.onTouched();
    this.updateOptionViews();
    this.onChange(this.selectedValues);
    this.selectionChange.emit(this.selectedValues);
    this.changeDetectorRef.markForCheck();
  }

  trackByValue(index: number, option: SelectableTileOption): SelectableTileValue {
    return option.value;
  }

  private updateOptionViews(): void {
    this.optionViews = this.options.map(option => ({
      ...option,
      selected: this.selectedValues.includes(option.value)
    }));
  }
}
